import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  AlertTitle,
  TextField,
  Tabs,
  Tab,
  Typography,
  Box
} from '@mui/material';

const ReviewUserModal = ({ show, onHide, user, onAction }) => {
  const [reviewNote, setReviewNote] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  if (!user) return null;

  const getRiskColor = (riskScore) => {
    if (riskScore >= 8) return 'error';
    if (riskScore >= 6) return 'warning';
    if (riskScore >= 4) return 'info';
    return 'success';
  };

  const getClassificationLabel = (classification) => {
    const labels = {
      'HIGH_RISK_CREEP': 'Critical Risk',
      'MODERATE_CREEP': 'Moderate Risk',
      'LOW_RISK': 'Low Risk',
      'APPROPRIATE_ACCESS': 'Appropriate Access'
    };
    return labels[classification] || classification;
  };

  const handleMarkAsReviewed = () => {
    onAction('mark_reviewed', user.id, { note: reviewNote });
    onHide();
  };

  const handleCreateTask = () => {
    onAction('create_task', user.id, { note: reviewNote });
    onHide();
  };

  const handleDismiss = () => {
    onAction('dismiss', user.id);
    onHide();
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        User Risk Review
      </DialogTitle>

      <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto', pt: 2 }}>
        {/* User Info Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>{user.name}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>{user.email}</Typography>
              {user.job_title && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user.job_title}
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={`Risk Score: ${user.risk_score}/10`}
                color={getRiskColor(user.risk_score)}
                size="small"
                sx={{ mb: 1 }}
              />
              <br />
              <Chip
                label={getClassificationLabel(user.classification)}
                size="small"
              />
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{user.group_count}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Groups</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{user.privileged_group_count}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Privileged</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>{user.unused_access_count}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Unused Apps</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>{user.groups_above_peer_avg}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Above Peers</Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs for detailed information */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Overview" />
            <Tab label={`Groups (${user.group_count})`} />
            <Tab label={`Apps (${user.app_count})`} />
            <Tab label="History" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        {activeTab === 0 && (
          <Box sx={{ pt: 2 }}>
            {/* AI Reasoning */}
            {user.reasoning && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>AI Analysis</AlertTitle>
                <Typography variant="body2">{user.reasoning}</Typography>
                {user.confidence && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    Confidence: {(user.confidence * 100).toFixed(0)}% |
                    Model: {user.model_used || 'Unknown'}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Risk Indicators */}
            {user.indicators && user.indicators.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Risk Indicators
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {user.indicators.map((indicator, idx) => (
                    <Chip key={idx} label={indicator} color="warning" size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Recommendations */}
            {user.recommendations && user.recommendations.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Recommended Actions
                </Typography>
                <List dense>
                  {user.recommendations.map((rec, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}

        {/* Groups Tab */}
        {activeTab === 1 && (
          <Box sx={{ pt: 2 }}>
            {/* Privileged Groups */}
            {user.privileged_groups && user.privileged_groups.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'error.main', mb: 1 }}>
                  Privileged Groups ({user.privileged_groups.length})
                </Typography>
                <List dense>
                  {user.privileged_groups.map((group, idx) => (
                    <ListItem key={idx} sx={{ bgcolor: 'error.dark', borderRadius: 1, mb: 0.5, opacity: 0.15 }}>
                      <ListItemText primary={group} primaryTypographyProps={{ variant: 'body2' }} />
                      <Chip label="High Risk" color="error" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Groups to Review */}
            {user.groups_to_review && user.groups_to_review.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 1 }}>
                  Groups Requiring Review ({user.groups_to_review.length})
                </Typography>
                <List dense>
                  {user.groups_to_review.map((group, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={group} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Unusual Groups */}
            {user.unusual_groups && user.unusual_groups.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'info.main', mb: 1 }}>
                  Unusual Groups ({user.unusual_groups.length})
                </Typography>
                <List dense>
                  {user.unusual_groups.map((group, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={group} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Peer Comparison */}
            {user.peer_count > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  <strong>Peer Comparison:</strong> This user has {user.groups_above_peer_avg} more
                  groups than the average of {user.peer_count} peers in similar roles
                  {user.peer_avg_groups && ` (avg: ${user.peer_avg_groups.toFixed(1)} groups)`}.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Apps Tab */}
        {activeTab === 2 && (
          <Box sx={{ pt: 2 }}>
            {/* Apps to Review */}
            {user.apps_to_review && user.apps_to_review.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 1 }}>
                  Applications Requiring Review ({user.apps_to_review.length})
                </Typography>
                <List dense>
                  {user.apps_to_review.map((app, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={app} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Never Used Apps */}
            {user.never_used_apps && user.never_used_apps.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'error.main', mb: 1 }}>
                  Never Used Applications ({user.never_used_apps.length})
                </Typography>
                <List dense>
                  {user.never_used_apps.map((app, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={app} primaryTypographyProps={{ variant: 'body2' }} />
                      <Chip label="Never Used" color="error" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Unused Apps (general) */}
            {user.unused_apps && user.unused_apps.length > 0 &&
             (!user.never_used_apps || user.never_used_apps.length === 0) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 1 }}>
                  Unused Applications ({user.unused_apps.length})
                </Typography>
                <List dense>
                  {user.unused_apps.map((app, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <ListItemText primary={app} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {user.days_since_last_use && (
              <Alert severity="info">
                <Typography variant="caption">
                  Last application usage: {user.days_since_last_use} days ago
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* History Tab */}
        {activeTab === 3 && (
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Analysis Information</Typography>
              <List dense>
                <ListItem sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Analysis Date:</Typography>
                        <Typography variant="body2">{user.analysis_date ? new Date(user.analysis_date).toLocaleString() : 'N/A'}</Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Status:</Typography>
                        <Chip
                          label={user.status}
                          color={user.status === 'pending' ? 'warning' : 'success'}
                          size="small"
                        />
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>AI Model:</Typography>
                        <Typography variant="body2">{user.model_used || 'Unknown'}</Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {user.confidence && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Confidence Score:</Typography>
                          <Typography variant="body2">{(user.confidence * 100).toFixed(0)}%</Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>

            <Alert severity="info">
              <Typography variant="caption">
                This analysis is based on current group memberships, application usage patterns,
                and peer comparisons. Review actions and status updates will be tracked here.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Review Notes */}
        <Box sx={{ mt: 3 }}>
          <TextField
            label="Review Notes (optional)"
            multiline
            rows={3}
            fullWidth
            placeholder="Add notes about your review decision..."
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={handleDismiss}>
          Dismiss
        </Button>
        <Button color="info" onClick={handleCreateTask}>
          Create Task
        </Button>
        <Button color="success" variant="contained" onClick={handleMarkAsReviewed}>
          Mark as Reviewed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewUserModal;
